import { Component } from '@angular/core';
import { UntilDestroy } from '@ngneat/until-destroy';
import { I18NextPipe } from 'angular-i18next';

import { PlanTypes } from 'mbs-ui-shared@models/PlanTypes.enum';
import { RestoreDatabaseModelForStepsHelper } from 'wizards/helpers/restore-database-helpers/restore-database-model-for-steps-helper';
import { RestoreDatabasePlanFromStepsHelpers } from 'wizards/helpers/restore-database-helpers/restore-database-plan-from-steps-helpers';
import { RestoreDatabaseFormModel } from 'wizards/models/databaseRestore/restore-database-form-model';
import { RestoreType } from 'wizards/models/restore-point-models';
import { RestoreAsDiskEnum } from 'wizards/models/type-data-models';
import { WizardHelpLink } from 'wizards/remote-management-wizards/strategy/wizard-help-link.class';

import { StepLoadInfo } from 'wizards/steps/StepBase.class';
import { WizardBaseClass } from '../wizard-base.class';

import { ScheduleAdvancedStepComponent } from 'wizards/steps/schedule-advanced-step/schedule-advanced-step.component';

import { NotificationsStepComponent } from 'wizards/steps/notifications-step/notifications-step.component';

import { RestoreSourceStepComponent } from 'wizards/steps/restore-source-step/restore-source-step.component';

import { BackupToRestoreStepComponent } from 'wizards/steps/backup-to-restore-step/backup-to-restore-step.component';

import { DestinationDBStepComponent } from 'wizards/steps/only-restore-steps/destination-db-step/destination-db-step.component';

import { EncryptionOptionsStepComponent } from 'wizards/steps/encription-options-step/encryption-options-step.component';

import { ScheduleStepComponent } from 'wizards/steps/schedule-step/schedule-step.component';

import { WizardComponent, WizardStepComponent } from 'mbs-ui-kit';

import { SQLServerInstanceStepComponent } from 'wizards/steps/sql-server-instance-step/sql-server-instance-step.component';

import { SelectComputerStepComponent } from 'wizards/steps/select-computer-step/select-computer-step.component';

import { PrePostActionsStepComponent } from 'wizards/steps/pre-post-actions-step/pre-post-actions-step.component';

import { WhereBackupStepComponent } from 'wizards/steps/where-backup-step/where-backup-step.component';

import { PlanNameStepComponent } from 'wizards/steps/plan-name-step/plan-name-step.component';

import { RestorePointStepComponent } from 'wizards/steps/restore-point-step/restore-point-step.component';

import { AsyncPipe, CommonModule } from '@angular/common';

import { FormsModule } from '@angular/forms';

type DatabaseRestoreWizardHelpDocumentationLinks =
  | 'welcome'
  | 'storage'
  | 'selectComputer'
  | 'selectBackup'
  | 'selectPoint'
  | 'instance'
  | 'source'
  | 'destination'
  | 'encryption'
  | 'schedule'
  | 'prePost'
  | 'notification';

@UntilDestroy()
@Component({
  selector: 'mbs-database-restore-wizard',
  templateUrl: './database-restore-wizard.component.html',
  imports: [
    AsyncPipe,
    BackupToRestoreStepComponent,
    CommonModule,
    DestinationDBStepComponent,
    EncryptionOptionsStepComponent,
    FormsModule,
    NotificationsStepComponent,
    PlanNameStepComponent,
    PrePostActionsStepComponent,
    RestorePointStepComponent,
    RestoreSourceStepComponent,
    SQLServerInstanceStepComponent,
    ScheduleAdvancedStepComponent,
    ScheduleStepComponent,
    SelectComputerStepComponent,
    WhereBackupStepComponent,
    WizardComponent,
    WizardStepComponent,
    I18NextPipe
  ]
})
export class DatabaseRestoreWizardComponent extends WizardBaseClass {
  public readonly backupToRestoreTitle = this.i18nPipe.transform('wizards:backupToRestoreTitle');
  public readonly restorePointTitle = this.i18nPipe.transform('wizards:restore_point_title');
  public readonly serverInstanceTitle = this.i18nPipe.transform('wizards:sql:sqlServerInstanceStepMenuTitle');

  protected readonly restoreAsDiskEnum = RestoreAsDiskEnum;

  public helpLink: WizardHelpLink<DatabaseRestoreWizardHelpDocumentationLinks>;
  public restoreType = RestoreType;

  get getInstances(): { Name: string }[] {
    return (
      this.loadedPlan?.DatabaseServerInstances || [
        { Name: this.FormModel?.sqlServerInstanceStep?.sqlServerInstanceName || this.mainService?.compName || '' }
      ]
    );
  }

  get isRestoreDatabase(): boolean {
    return this.planType === PlanTypes.RestoreDataBasePlan;
  }

  constructor() {
    super();

    this.FormModel = new RestoreDatabaseFormModel(this.isNBF, this.isRestoreDatabase);

    this.initSubscribes();
  }

  updateStepData(plan, isDefault = false): void {
    this.loadedPlan = plan;
    if (this.isEdit && !this.isRDMode) this.mainService.GetPlanNotificationOptions();

    if (isDefault) return this.setDefaultValue();

    this.FormModel = RestoreDatabaseModelForStepsHelper.restoreDatabaseModelFromPlan(plan, this.defaultSettings());

    this.stepsManager.showEncryptionOptionsStepIfNeed();
  }

  protected getPlanForSaving(): any {
    return RestoreDatabasePlanFromStepsHelpers.getUpdatedPlan(this.loadedPlan.Plan, this.FormModel, this.defaultSettings());
  }

  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  protected loadInfoHandlerFinally(event?: StepLoadInfo): void {
    this.cdr.detectChanges();
  }

  getRAWHelpLinks(): Record<DatabaseRestoreWizardHelpDocumentationLinks, string> {
    const prefix = this.isRestoreDatabase ? 'restore/mssql/console-database' : 'restore/mssql/console-files';

    return {
      welcome: `${prefix}#planname`,
      storage: `${prefix}#storage`,
      selectComputer: `${prefix}#comp`,
      selectBackup: `${prefix}#backup`,
      selectPoint: `${prefix}#rp`,
      instance: `${prefix}#instance`,
      source: `${prefix}#source`,
      destination: `${prefix}#destination`,
      encryption: `${prefix}#encryption`,
      schedule: `${prefix}#schedule`,
      prePost: `${prefix}#prepost`,
      notification: `${prefix}#notification`
    };
  }
}
